import React from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import { CheckCircleOutline, ErrorOutline, InfoOutlined } from '@mui/icons-material';
import type { Notification } from './types';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onMarkAsRead }) => {
  const isImportant = notification.status === 'important';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        p: 2.5,
        borderRadius: '12px',
        border: isImportant
          ? '1px solid rgba(239, 68, 68, 0.35)'
          : '1px solid rgba(255, 255, 255, 0.08)',
        bgcolor: isImportant
          ? 'rgba(239, 68, 68, 0.07)'
          : 'rgba(255, 255, 255, 0.04)',
        transition: 'background-color 0.2s ease',
        opacity: notification.isRead ? 0.5 : 1,
      }}
    >
      {/* Иконка */}
      <Box
        sx={{
          flexShrink: 0,
          mt: 0.25,
          color: isImportant ? '#ef4444' : '#60a5fa',
        }}
      >
        {isImportant ? (
          <ErrorOutline sx={{ fontSize: 22 }} />
        ) : (
          <InfoOutlined sx={{ fontSize: 22 }} />
        )}
      </Box>

      {/* Текст */}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
          <Chip
            label={isImportant ? 'Важно' : 'Уведомление'}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.7rem',
              fontWeight: 600,
              bgcolor: isImportant ? 'rgba(239, 68, 68, 0.2)' : 'rgba(96, 165, 250, 0.15)',
              color: isImportant ? '#fca5a5' : '#93c5fd',
              border: isImportant ? '1px solid rgba(239,68,68,0.3)' : '1px solid rgba(96,165,250,0.2)',
            }}
          />
          <Typography
            variant="caption"
            sx={{ color: '#6b7280', fontSize: '0.72rem' }}
          >
            От: <Box component="span" sx={{ color: '#9ca3af', fontWeight: 600 }}>{notification.sender}</Box>
          </Typography>
          {notification.isRead && (
            <Chip
              label="Прочитано"
              size="small"
              icon={<CheckCircleOutline sx={{ fontSize: '14px !important', color: '#4ade80 !important' }} />}
              sx={{
                height: 20,
                fontSize: '0.7rem',
                bgcolor: 'rgba(74, 222, 128, 0.1)',
                color: '#4ade80',
                border: '1px solid rgba(74,222,128,0.2)',
              }}
            />
          )}
        </Box>

        <Typography
          variant="body2"
          sx={{
            color: notification.isRead ? '#6b7280' : '#e5e7eb',
            lineHeight: 1.6,
            fontSize: '0.875rem',
          }}
        >
          {notification.message}
        </Typography>
      </Box>

      {/* Кнопка */}
      {!notification.isRead && (
        <Button
          size="small"
          variant="outlined"
          onClick={() => onMarkAsRead(notification.id)}
          sx={{
            flexShrink: 0,
            fontSize: '0.75rem',
            textTransform: 'none',
            whiteSpace: 'nowrap',
            borderColor: 'rgba(255,255,255,0.2)',
            color: '#d1d5db',
            '&:hover': {
              borderColor: '#4ade80',
              color: '#4ade80',
              bgcolor: 'rgba(74, 222, 128, 0.05)',
            },
          }}
        >
          Прочитано
        </Button>
      )}
    </Box>
  );
};

export default NotificationItem;
